import * as React from "react";

// Chrome's one-tap install offer (beforeinstallprompt). It fires ONCE, early — often before the
// Notifications page exists — so it is caught here, at start-up, and held for whichever page offers
// the Install button. Safari and Firefox never fire it; that is what the per-platform steps are for.
export interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

let held: InstallPromptEvent | null = null;
let installed = false;
const listeners = new Set<() => void>();
const changed = () => listeners.forEach((l) => l());

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    // Held rather than allowed to show: the browser's own bar appears at a moment of its choosing,
    // and the app offers the choice where it explains what installing is for.
    e.preventDefault();
    held = e as InstallPromptEvent;
    changed();
  });
  window.addEventListener("appinstalled", () => {
    installed = true;
    held = null;
    changed();
  });
}

export function useInstallPrompt(): { prompt: InstallPromptEvent | null; justInstalled: boolean; use: () => Promise<boolean> } {
  const snap = React.useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => (held ? 1 : 0) + (installed ? 2 : 0),
    () => 0,
  );
  return {
    prompt: snap & 1 ? held : null,
    justInstalled: (snap & 2) === 2,
    use: async () => {
      const p = held;
      if (!p) return false;
      await p.prompt();
      const choice = await p.userChoice;
      // The browser will not offer the same prompt object twice, accepted or not.
      held = null;
      if (choice.outcome === "accepted") installed = true;
      changed();
      return choice.outcome === "accepted";
    },
  };
}

// ── Is the app already installed here, and opening it ───────────────────────────────────────────
//
// "Installed" is known two ways: this tab just installed it (appinstalled / the accepted prompt), or the
// browser says so — navigator.getInstalledRelatedApps, which Chrome on Android answers for a web app
// whose manifest names itself in related_applications (both manifests do). Safari and Firefox answer
// neither, so there it stays unknown and the page gives the steps instead of a button that cannot work.

interface RelatedApp {
  platform: string;
  url?: string;
}

let relatedChecked = false;
let relatedInstalled = false;

async function checkRelated(): Promise<void> {
  if (relatedChecked) return;
  relatedChecked = true;
  const nav = navigator as Navigator & { getInstalledRelatedApps?: () => Promise<RelatedApp[]> };
  if (typeof nav.getInstalledRelatedApps !== "function") return;
  try {
    const apps = await nav.getInstalledRelatedApps();
    if (apps.some((a) => a.platform === "webapp")) {
      relatedInstalled = true;
      changed();
    }
  } catch {
    /* Not allowed here (a frame, an insecure origin): unknown, not "not installed". */
  }
}

/** Whether this device has the app: true when known; false means not known to be (never "absent"). */
export function useAppInstalled(): boolean {
  const { justInstalled } = useInstallPrompt();
  React.useEffect(() => {
    void checkRelated();
  }, []);
  const known = React.useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => relatedInstalled,
    () => false,
  );
  return justInstalled || known;
}

/**
 * The address the installed app starts at: the origin root on a webmail host (its own manifest's
 * start_url "/"), else this console's base. Opened in a NEW window, which is the navigation a browser
 * hands to an installed app that captures its links (Chrome on Android hands it to the installed app;
 * Chrome and Edge on a computer open the app's window). Where the browser keeps it, the page says where
 * the icon is — the caller shows that beside the button.
 */
export function openInstalledApp(): void {
  const start = window.location.host.startsWith("webmail.") ? "/" : import.meta.env.BASE_URL;
  window.open(new URL(start, window.location.origin).toString(), "_blank", "noopener");
}
